
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Image } from 'react-bootstrap';
import api from '../utils/api';
import 'bootstrap/dist/css/bootstrap.min.css';

const SeatBookingModal = ({ show, handleClose, seat, onBookingSuccess }) => {
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    
    // State to hold the actual File objects
    const [paymentScreenshotFiles, setPaymentScreenshotFiles] = useState([]);
    const [aadharCardPhotoFile, setAadharCardPhotoFile] = useState(null);
    
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        if (show && seat && seat.schedules) {
            const firstAvailableSlot = seat.schedules.find(s => !s.isOccupied);
            setSelectedSlot(firstAvailableSlot || null);
        }
    }, [show, seat]);

    const createMergedImage = (images) => {
        return new Promise(resolve => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const padding = 20;

            let totalHeight = 0;
            let maxWidth = 0;
            
            const loadedImages = images.map(img => new window.Image()); // Use window.Image
            const loadPromises = images.map((imgSrc, index) => {
                return new Promise(imgResolve => {
                    loadedImages[index].onload = () => {
                        totalHeight += loadedImages[index].height + padding;
                        if (loadedImages[index].width > maxWidth) {
                            maxWidth = loadedImages[index].width;
                        }
                        imgResolve();
                    };
                    loadedImages[index].src = imgSrc;
                });
            });

            Promise.all(loadPromises).then(() => {
                canvas.width = maxWidth;
                canvas.height = totalHeight;
                
                let currentY = 0;
                loadedImages.forEach(img => {
                    ctx.drawImage(img, 0, currentY);
                    currentY += img.height + padding;
                });
                
                canvas.toBlob(resolve, 'image/jpeg');
            });
        });
    };
    
    const handleGeneratePayment = async () => {
        if (!userId || !selectedSlot) {
            setError('You must be logged in and select a slot.');
            return;
        }

        if (selectedSlot.isOccupied) {
            setError('This time slot is already booked. Please choose another one.');
            return;
        }

        setLoading(true);
        setError(null);

        const now = new Date();
        let startTime = new Date(now.setHours(selectedSlot.startTimeHour, 0, 0, 0));
        let endTime;

        if (selectedSlot.isSubscription) {
            endTime = new Date(startTime);
            endTime.setMonth(endTime.getMonth() + 1);
        } else if (selectedSlot.startTimeHour > selectedSlot.endTimeHour) {
            endTime = new Date(startTime);
            endTime.setDate(endTime.getDate() + 1);
            endTime.setHours(selectedSlot.endTimeHour, 0, 0, 0);
        } else {
            endTime = new Date(startTime);
            endTime.setHours(selectedSlot.endTimeHour, 0, 0, 0);
        }
        
        try {
            const response = await api.post(`/seats/${seat._id}/book`, {
                userId,
                bookingSlot: { startTime, endTime },
                slotName: selectedSlot.slotName,
                isSubscription: selectedSlot.isSubscription
            });
            
            setPaymentDetails({
                ...response.data.paymentDetails,
                seatId: response.data.seatId,
                userId: response.data.userId,
                bookingId: response.data.bookingId,
            });
        } catch (err) {
            console.error('Booking failed:', err.response?.data?.message || err.message);
            setError(err.response?.data?.message || err.message || 'Failed to generate payment details. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleConfirmBooking = async () => {
        if (!paymentScreenshotFiles.length || !aadharCardPhotoFile) {
            setError('Please attach all required documents: payment screenshot(s) and Aadhar card photo.');
            return;
        }

        setPaymentProcessing(true);
        setError(null);

        try {
            const formData = new FormData();
            const bookingData = {
                seatId: paymentDetails.seatId,
                userId: paymentDetails.userId,
                bookingDetails: paymentDetails.bookingDetails
            };
            
            formData.append('bookingData', JSON.stringify(bookingData));
            
            const paymentImageUrls = paymentScreenshotFiles.map(file => URL.createObjectURL(file));
            const aadharImageUrl = URL.createObjectURL(aadharCardPhotoFile);
            
            const mergedImageBlob = await createMergedImage([...paymentImageUrls, aadharImageUrl]);
            
            formData.append('combinedScreenshot', mergedImageBlob, 'combined_docs.jpg');
            
            [...paymentImageUrls, aadharImageUrl].forEach(URL.revokeObjectURL);
            
            await api.post('/seats/confirm', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (onBookingSuccess) {
                onBookingSuccess();
            }
        } catch (err) {
            console.error('Confirmation failed:', err.response?.data?.message || err.message);
            setError(err.response?.data?.message || err.message || 'Failed to confirm booking. Please try again.');
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handleModalClose = () => {
        setPaymentDetails(null);
        setError(null);
        setPaymentScreenshotFiles([]);
        setAadharCardPhotoFile(null);
        handleClose();
    };

    const handlePaymentFileChange = (e) => {
        setPaymentScreenshotFiles(Array.from(e.target.files));
    };

    const handleAadharFileChange = (e) => {
        setAadharCardPhotoFile(e.target.files[0]);
    };
    
    const filteredSchedules = (seat && seat.schedules) ? seat.schedules : [];
    const isSlotOccupied = selectedSlot?.isOccupied;

    return (
        <Modal show={show} onHide={handleModalClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{paymentDetails ? 'Scan to Pay' : `Book Seat ${seat?.seatNumber}`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                
                {!paymentDetails ? (
                    <>
                        {filteredSchedules.length === 0 ? (
                            <p>No schedules available for this seat.</p>
                        ) : (
                            <>
                                <p>You are about to book seat <strong>{seat?.seatNumber}</strong>.</p>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select Time Slot</Form.Label>
                                        <Form.Select 
                                            value={selectedSlot?.slotName || ''} 
                                            onChange={(e) => setSelectedSlot(filteredSchedules.find(s => s.slotName === e.target.value))}
                                        >
                                            <option value="" disabled>Choose a time slot</option>
                                            {filteredSchedules.map(slot => (
                                                <option 
                                                    key={slot.slotName} 
                                                    value={slot.slotName}
                                                    disabled={slot.isOccupied}
                                                >
                                                    {slot.label} - ₹{slot.price} 
                                                    {slot.isOccupied && " (Occupied)"}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Form>
                            </>
                        )}
                    </>
                ) : (
                    <div className="text-center">
                        <p className="lead">Scan this QR code with any UPI app to pay <strong>₹{selectedSlot?.price}</strong></p>
                        <Image src={paymentDetails.qrCodeDataUrl} fluid style={{ maxWidth: '250px' }} />
                        <p className="mt-3">
                            <a href={paymentDetails.upiLink} target="_blank" rel="noopener noreferrer">
                                Pay via UPI app
                            </a>
                        </p>
                        <Alert variant="info" className="mt-4">
                            <strong>After paying, please attach a screenshot of the payment and a photo of your Aadhar card.</strong>
                        </Alert>
                        
                        <Form.Group controlId="formFilePayment" className="mb-3">
                            <Form.Label>Attach Payment Screenshot(s)</Form.Label>
                            <Form.Control type="file" onChange={handlePaymentFileChange} multiple accept="image/*" />
                        </Form.Group>
                        
                        <Form.Group controlId="formFileAadhar" className="mb-3">
                            <Form.Label>Attach Aadhar Card Photo</Form.Label>
                            <Form.Control type="file" onChange={handleAadharFileChange} accept="image/*" />
                        </Form.Group>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                    {paymentDetails ? 'Cancel Booking' : 'Close'}
                </Button>
                {!paymentDetails ? (
                    <Button 
                        variant="primary" 
                        onClick={handleGeneratePayment} 
                        disabled={loading || !selectedSlot || isSlotOccupied}>
                        {loading ? <Spinner animation="border" size="sm" /> : `Pay ₹${selectedSlot?.price || 0} & Book`}
                    </Button>
                ) : (
                    <Button 
                        variant="success" 
                        onClick={handleConfirmBooking} 
                        disabled={paymentProcessing || !paymentScreenshotFiles.length || !aadharCardPhotoFile}>
                        {paymentProcessing ? <Spinner animation="border" size="sm" /> : 'Confirm Payment'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default SeatBookingModal;